import axios from "axios";
import swal from "sweetalert";
import config from "../../config.json";

export async function fetchAllMenus() {
  try {
    const response = await axios.get(`${config.dev}/v2/menu/getAll`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
      },
    });
    return response.data;
  } catch (err) {
    console.error("Erreur lors de la récupération des menus:", err);
  }
}

/**
 * Update display order of menus (for drag-and-drop). Order in client = order shown to customers.
 * @param {string[]} menuIds - Ordered array of menu IDs (index = display order)
 */
export async function updateMenusOrder(menuIds) {
  try {
    const response = await axios.put(
      `${config.dev}/v2/menu/update-order`,
      { menuIds },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
        },
      }
    );
    return response.data;
  } catch (err) {
    console.error("Erreur lors de la mise à jour de l'ordre des menus:", err);
    throw err;
  }
}

/**
 * Quick-create a category from a single (English) title — used by the
 * "+ New category" affordance in the Manage menu picker. No photo.
 */
export async function createCategoryQuick(title) {
  const formData = new FormData();
  formData.append(
    "data",
    JSON.stringify({
      categoryDetails: [
        { language: "en", categoryTitle: String(title).trim(), categoryDescription: "" },
      ],
      displayOrientation: "vertical",
    })
  );
  const response = await axios.post(`${config.dev}/v2/categories/create`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
    },
  });
  return response.data;
}

/**
 * Quick-create a product from a single (English) title — used by the
 * "+ New product" affordance in the Manage menu picker. Price 0, no photo;
 * details can be completed later from the Products page.
 */
export async function createProductQuick(title) {
  const formData = new FormData();
  formData.append(
    "data",
    JSON.stringify({
      price: 0,
      haveMultipleSize: false,
      extraSizes: [],
      productDetails: [
        { language: "en", productTitle: String(title).trim(), productDescription: "" },
      ],
      tags: [],
    })
  );
  const response = await axios.post(`${config.dev}/v2/products/create`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
    },
  });
  return response.data;
}

export const fetchCategories = async () => {
  try {
    const response = await axios.get(`${config.dev}/v2/categories/retrieve/`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`, // Récupérer le token
      },
    });

    const data = response?.data || [];
    const activeCategories = data.filter(
      (category) => category.isArchived === false
    );

    return activeCategories;
  } catch (err) {
    console.error("Error fetching products:", err);

    return err;
  }
};

export async function syncProductChoicesAndItems(productId, choiceList, hiddenChoiceItems) {
  const token = localStorage.getItem("tokenLogin");
  const response = await axios.post(
    `${config.dev}/v2/products/addChoice/${productId}`,
    {
      choiceList: Array.isArray(choiceList) ? choiceList : [],
      hiddenChoiceItems: Array.isArray(hiddenChoiceItems) ? hiddenChoiceItems : [],
      replace: true,
    },
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  return response.data;
}

export function hiddenChoiceItemsFromKeep(children, keptChoiceIds, keptItemsByChoice) {
  const keptChoices = Array.isArray(keptChoiceIds)
    ? keptChoiceIds
    : (children || []).map((child) => child.id);
  return (children || [])
    .filter((child) => keptChoices.includes(child.id))
    .map((child) => {
      const all = (child.products || []).map((p) => p.id);
      const kept = Array.isArray(keptItemsByChoice?.[child.id])
        ? keptItemsByChoice[child.id].filter((id) => all.includes(id))
        : all;
      const hidden = all.filter((id) => !kept.includes(id));
      return hidden.length ? { choice: child.id, items: hidden } : null;
    })
    .filter(Boolean);
}

export const fetchProducts = async () => {
  try {
    const response = await axios.get(`${config.dev}/v2/products/retrieve`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`, // Récupérer le token
      },
    });
    const data = response?.data || [];

    const activeProducts = data.filter(
      (product) => product.isArchived === false
    );

    return activeProducts;
  } catch (err) {
    console.error("Error fetching products:", err);
    return err;
  }
};

export const addCategoriesToMenu = async (menuId, categories) => {
  try {
    const response = await axios.post(`${config.dev}/v2/menu/addCategory`, {
      menuId,
      categories: categories.map((c) => ({
        ...c,
        hideTitleFromClient: c.hideTitleFromClient ?? false,
        hideFromSummary: c.hideFromSummary ?? false,
      })),
    });
    return response.data;
  } catch (error) {
    console.error("Erreur lors de l'ajout des catégories:", error);
    throw error;
  }
};

export const fetchCategory = async (id) => {
  try {
    const response = await axios.get(
      `${config.dev}/v2/categories/retrieve/${id}`
    );
    const data = response?.data || {};
    return data;
  } catch (err) {
    console.error("Error fetching category:", err);
  }
};

export async function fetchMenuById(id) {
  try {
    const response = await axios.get(
      `${config.dev}/v2/menu/getMenuByIdSimple/${id}`
    );
    return response.data;
  } catch (err) {
    console.error("Erreur lors de la récupération des menus:", err);
  }
}

export async function deleteCategoryFromMenu(menuId, categoryIds) {
  try {
    const response = await axios.delete(
      `${config.dev}/v2/menu/delete/${menuId}`,
      {
        data: { categoryIds },
      }
    );
    return response.data;
  } catch (err) {
    console.error("Erreur lors de la suppression des catégorie:", err);
  }
}

export const restoreMenu = async (id) => {
  try {
    await axios.put(
      `${config.dev}/v2/menu/restore/${id}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
        },
      }
    );
    swal("SUCCESS", "Menu has been successfully restored.", "success");
  } catch (error) {
    const serverMessage = error.response?.data?.message;
    if (serverMessage === "Menu not found") {
      swal("ERROR", "The requested menu does not exist.", "error");
    } else if (error.request && !error.response) {
      swal(
        "ERROR",
        "No response from the server. Check your connection.",
        "error"
      );
    } else {
      swal(
        "ERROR",
        serverMessage || "Failed to restore menu: " + error.message,
        "error"
      );
    }
    throw error;
  }
};

export const deleteMenuForever = async (id) => {
  const response = await axios.delete(`${config.dev}/v2/menu/delete-permanent/${id}`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
    },
  });
  return response.data;
};

export const hideMenu = async (id) => {
  try {
    await axios.put(
      `${config.dev}/v2/menu/hide/${id}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
        },
      }
    ).then(() => {
      swal("SUCCESS", "Menu is in archived", "success");
    });
  } catch (error) {
    swal("ERROR", "" + error, "error");
  }
};

/**
 * Enable or disable a menu. Disabled menus are hidden from customers.
 * @param {string} menuId
 * @param {boolean} isEnabled
 */
export async function toggleMenuEnabled(menuId, isEnabled) {
  try {
    const response = await axios.put(
      `${config.dev}/v2/menu/toggle-enabled/${menuId}`,
      { isEnabled },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
        },
      }
    );
    return response.data;
  } catch (err) {
    console.error("Error toggling menu enabled:", err);
    throw err;
  }
}

export const fetchCategoryProducts = async (menuId, categoryId) => {
  try {
    const response = await axios.get(
      `${config.dev}/v2/menu/getProductsByCategory/${menuId}/${categoryId}`
    );
    const data = response?.data || {};
    return data;
  } catch (err) {
    console.error("Error fetching category:", err);
  }
};

export async function deleteProductFromCategory(
  menuId,
  categoryId,
  productIds
) {
  try {
    const response = await axios.delete(
      `${config.dev}/v2/menu/deleteProduct/${menuId}/${categoryId}`,
      {
        data: { productIds: productIds },
      }
    );
    return response.data;
  } catch (err) {
    console.error("Erreur lors de la suppression des catégorie:", err);
  }
}

export async function addProducToCategory(menuId, categoryId, productList) {
  console.log("productList", productList);
  console.log("menuId", menuId);
  console.log("categoryId", categoryId);

  {
    try {
      const response = await axios.post(
        `${config.dev}/v2/menu/addProduct/${menuId}/${categoryId}`,
        productList
      );
      return response.data;
    } catch (err) {
      console.error("Erreur lors de l'ajout des produits à la catégorie:", err);
    }
  }
}

export const updateCategoryOrder = async (menuId, categoryId, newOrder) => {
  const token = localStorage.getItem("tokenLogin");
  const response = await axios.put(
    `${config.dev}/v2/menu/update_cat_order/${menuId}/${categoryId}`,
    { newOrder },
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  return response.data;
};

/**
 * When true, the category section title is hidden on the customer menu app.
 * Backend route (to implement if missing): PUT .../v2/menu/category-title-visibility/:menuId/:categoryId
 */
export async function updateMenuCategoryHideTitleFromClient(
  menuId,
  categoryId,
  hideTitleFromClient
) {
  const token = localStorage.getItem("tokenLogin");
  const response = await axios.put(
    `${config.dev}/v2/menu/category-title-visibility/${menuId}/${categoryId}`,
    { hideTitleFromClient },
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  return response.data;
}

/**
 * When true, the category is omitted from the customer app's summary list (this menu only).
 */
export async function updateMenuCategoryHideFromSummary(
  menuId,
  categoryId,
  hideFromSummary
) {
  const token = localStorage.getItem("tokenLogin");
  const response = await axios.put(
    `${config.dev}/v2/menu/category-summary-visibility/${menuId}/${categoryId}`,
    { hideFromSummary },
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  return response.data;
}

/**
 * Set optional summary click target for one category in a menu.
 * If subcategory is set, frontend jumps to that subcategory first.
 */
export async function updateMenuCategorySummaryTarget(
  menuId,
  categoryId,
  summaryTargetCategoryId,
  summaryTargetSubCategoryId
) {
  const token = localStorage.getItem("tokenLogin");
  const response = await axios.put(
    `${config.dev}/v2/menu/category-summary-target/${menuId}/${categoryId}`,
    {
      summaryTargetCategoryId: summaryTargetCategoryId || null,
      summaryTargetSubCategoryId: summaryTargetSubCategoryId || null,
    },
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  return response.data;
}

/**
 * When true, the subcategory title is hidden on the customer menu app (for this menu).
 * Backend route (to implement if missing): PUT .../v2/menu/subcategory-title-visibility/:menuId/:categoryId/:subCategoryId
 */
export async function updateMenuSubcategoryHideTitleFromClient(
  menuId,
  categoryId,
  subCategoryId,
  hideTitleFromClient
) {
  const token = localStorage.getItem("tokenLogin");
  const response = await axios.put(
    `${config.dev}/v2/menu/subcategory-title-visibility/${menuId}/${categoryId}/${subCategoryId}`,
    { hideTitleFromClient },
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  return response.data;
}

/**
 * Per-menu: which products of a subcategory are hidden in this menu.
 * Replaces the whole list; pass [] to show every product again.
 */
export async function updateMenuSubcategoryProductsVisibility(
  menuId,
  categoryId,
  subCategoryId,
  hiddenProductIds,
  options = {}
) {
  const token = localStorage.getItem("tokenLogin");
  const body = {
    hiddenProductIds: Array.isArray(hiddenProductIds) ? hiddenProductIds : [],
  };
  if (options.hideSubcategory === true) body.hideSubcategory = true;
  if (options.hideSubcategory === false) body.hideSubcategory = false;
  const response = await axios.put(
    `${config.dev}/v2/menu/subcategory-products-visibility/${menuId}/${categoryId}/${subCategoryId}`,
    body,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }
  );
  return response.data;
}

export const updateProductOrder = async (
  menuId,
  categoryId,
  productId,
  newOrder
) => {
  const token = localStorage.getItem("tokenLogin");
  const mid = String(menuId);
  const cid = String(categoryId);
  const pid = String(productId);
  try {
    const response = await axios.put(
      `${config.dev}/v2/menu/update_product_order/${mid}/${cid}/${pid}`,
      { newOrder },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    if (response.data) {
      return response.data;
    }
  } catch (error) {
    console.error("updateProductOrder:", error.response?.data || error.message);
    throw error;
  }
};

export const assignItemsToChoice = async (choiceId, itemIds) => {
  try {
    if (!Array.isArray(itemIds)) {
      throw new Error("itemIds must be an array");
    }

    const response = await axios.post(
      `${config.dev}/v2/choices/additems/${choiceId}`,
      {
        itemList: itemIds,
      }
    );

    if (response.data) {
      // swal("Succès", "Items ajoutés avec succès", "success");
      return response.data;
    }
  } catch (error) {
    console.error("assignItemsToChoice error:", error);
    swal(
      "ERROR",
      error.response?.data?.message || error.message,
      "error"
    );
    throw error;
  }
};

export const removeItemFromChoice = async (choiceId, itemId) => {
  try {
    const response = await axios.delete(
      `${config.dev}/v2/choices/deleteItem/${choiceId}/${itemId}`
    );

    if (response.data) {
      // swal("Succès", "Item supprimé du choice", "success");
      return response.data;
    }
  } catch (error) {
    console.error("removeItemFromChoice error:", error);
    swal(
      "ERROR",
      error.response?.data?.message || error.message,
      "error"
    );
    throw error;
  }
};

